/**
 * Party Bundle Landing Page - Interactive Features
 * Handles quantity controls, cart updates, and minimum purchase validation
 */

class PartyBundleManager {
  constructor() {
    this.MIN_PURCHASE_AMOUNT = 19900; // $199.00 in cents
    this.cartData = {};
    this.productPrices = {};
    
    this.init();
  }

  init() {
    this.cacheElements();
    this.attachEventListeners();
    this.updateCartDisplay();
  }

  cacheElements() {
    this.quantityMinusButtons = document.querySelectorAll('.quantity-minus');
    this.quantityPlusButtons = document.querySelectorAll('.quantity-plus');
    this.quantityInputs = document.querySelectorAll('.quantity-input');
    this.checkoutButton = document.getElementById('checkout-btn');
    this.cartTotalDisplay = document.getElementById('cart-total');
    this.productCards = document.querySelectorAll('.product-card');
    this.progressFill = document.getElementById('progress-fill');
    this.progressPercent = document.getElementById('progress-percent');
    this.progressBar = document.querySelector('.progress-bar');
    this.progressMarker = document.getElementById('progress-marker');
    this.progressCurrent = document.getElementById('progress-current');
    this.progressGoal = document.getElementById('progress-goal');
    this.upsellMessage = document.getElementById('upsell-message');
  }

  attachEventListeners() {
    this.quantityMinusButtons.forEach(btn => {
      btn.addEventListener('click', (e) => this.decreaseQuantity(e));
    });

    this.quantityPlusButtons.forEach(btn => {
      btn.addEventListener('click', (e) => this.increaseQuantity(e));
    });

    this.checkoutButton.addEventListener('click', (e) => this.handleCheckout(e));

    // Store product prices from the DOM
    this.productCards.forEach(card => {
      const productId = card.dataset.productId;
      const priceText = card.querySelector('.product-price').textContent;
      const priceInCents = this.parsePriceTocents(priceText);
      this.productPrices[productId] = priceInCents;
    });
  }

  parsePriceTocents(priceText) {
    // Remove currency symbols and convert to cents
    const price = parseFloat(priceText.replace(/[^0-9.]/g, ''));
    return Math.round(price * 100);
  }

  increaseQuantity(e) {
    const productId = e.target.dataset.productId;
    const input = document.querySelector(`.quantity-input[data-product-id="${productId}"]`);
    const currentValue = parseInt(input.value) || 0;
    input.value = currentValue + 1;
    this.updateCartDisplay();
  }

  decreaseQuantity(e) {
    const productId = e.target.dataset.productId;
    const input = document.querySelector(`.quantity-input[data-product-id="${productId}"]`);
    const currentValue = parseInt(input.value) || 0;
    if (currentValue > 0) {
      input.value = currentValue - 1;
      this.updateCartDisplay();
    }
  }

  calculateCartTotal() {
    let total = 0;
    this.quantityInputs.forEach(input => {
      const productId = input.dataset.productId;
      const quantity = parseInt(input.value) || 0;
      const price = this.productPrices[productId] || 0;
      total += quantity * price;
    });
    return total;
  }

  formatCurrency(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

    updateCartDisplay() {
    const cartTotal = this.calculateCartTotal();
    this.cartTotalDisplay.textContent = this.formatCurrency(cartTotal);

    this.updateCheckoutButtonState(cartTotal);
    this.updateProgressMessage(cartTotal);

    this.updateProgressBar(cartTotal);
    this.updateUpsellMessage(cartTotal);
    }

    updateProgressMessage(cartTotal) {
        if (!this.progressMessage) return;

        if (cartTotal >= this.MIN_PURCHASE_AMOUNT) {
            this.progressMessage.textContent = "You're eligible for checkout 🎉";
            this.progressMessage.classList.remove('warning');
            this.progressMessage.classList.add('success');
        } else {
            const remaining = this.MIN_PURCHASE_AMOUNT - cartTotal;
            this.progressMessage.textContent = `You're ${this.formatCurrency(remaining)} away from checkout`;
            this.progressMessage.classList.remove('success');
            this.progressMessage.classList.add('warning');
        }
    }
    updateProgressBar(cartTotal) {
    if (!this.progressFill || !this.progressPercent) return;

    const percentage = Math.min((cartTotal / this.MIN_PURCHASE_AMOUNT) * 100, 100);

    this.progressFill.style.width = percentage + '%';
    this.progressPercent.textContent = `${Math.round(percentage)}%`;

    if (this.progressBar) {
      this.progressBar.setAttribute('aria-valuenow', Math.round(percentage));
      this.progressBar.classList.toggle('complete', percentage >= 100);
    }

    if (this.progressMarker) {
      this.progressMarker.style.left = percentage + '%';
    }

    if (this.progressCurrent) {
      this.progressCurrent.textContent = this.formatCurrency(cartTotal);
    }

    if (this.progressGoal) {
      this.progressGoal.textContent = `${this.formatCurrency(this.MIN_PURCHASE_AMOUNT)} goal`;
    }
    }
    
    updateUpsellMessage(cartTotal) {
        if (!this.upsellMessage) return;

        if (cartTotal >= this.MIN_PURCHASE_AMOUNT) {
            this.upsellMessage.textContent = "Checkout is unlocked";
            this.upsellMessage.classList.add('success');
            return;
        }

        const remaining = this.MIN_PURCHASE_AMOUNT - cartTotal;

        // Find cheapest product
        let cheapestPrice = Infinity;

        Object.values(this.productPrices).forEach(price => {
            if (price < cheapestPrice) {
            cheapestPrice = price;
            }
        });

        if (cheapestPrice !== Infinity && remaining <= cheapestPrice) {
            this.upsellMessage.textContent = "Add 1 more item to unlock checkout";
        } else {
            this.upsellMessage.textContent = `You're ${this.formatCurrency(remaining)} away from checkout`;
        }

        this.upsellMessage.classList.remove('success');
    }

  updateCheckoutButtonState(cartTotal) {
    if (cartTotal >= this.MIN_PURCHASE_AMOUNT) {
      this.checkoutButton.disabled = false;
      this.checkoutButton.style.opacity = '1';
      this.checkoutButton.style.cursor = 'pointer';
    } else {
      this.checkoutButton.disabled = true;
      this.checkoutButton.style.opacity = '0.6';
      this.checkoutButton.style.cursor = 'not-allowed';
    }
  }

  handleCheckout(e) {
    const cartTotal = this.calculateCartTotal();

    if (cartTotal < this.MIN_PURCHASE_AMOUNT) {
      e.preventDefault();
      alert(`Minimum purchase of $199.00 required. Current total: ${this.formatCurrency(cartTotal)}`);
      return;
    }

    // Build cart data and add items
    this.addItemsToCart();
  }

  addItemsToCart() {
    const cartItems = [];

    this.quantityInputs.forEach(input => {
      const quantity = parseInt(input.value) || 0;
      if (quantity > 0) {
        const productCard = input.closest('.product-card');
        const variantId = productCard.dataset.variantId;

        cartItems.push({
        quantity: quantity,
        id: variantId
        });
      }
    });

    if (cartItems.length === 0) {
      alert('Please select at least one product.');
      return;
    }

    // Use Shopify's cart API to add items
    this.addToCart(cartItems);
  }

  addToCart(items) {
    const body = JSON.stringify({
      items: items
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: body
    })
    .then(response => response.json())
    .then(data => {
      // Redirect to cart page
      window.location.href = '/cart';
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      alert('Error adding items to cart. Please try again.');
    });
  }
}

// Initialize Party Bundle Manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PartyBundleManager();
  });
} else {
  new PartyBundleManager();
}
